export interface AuthenticatedUser {
  access_token: string;
  [key: string]: unknown;
}

interface SocialLoginDeps<User> {
  fetchCurrentUser: () => Promise<User>;
  persistSession: (token: string, user: User) => void;
  setAuthToken: (token: string) => void;
  clearAuth: () => void;
}

export const sanitizeNextPath = (nextPath?: string | null): string | null => {
  if (!nextPath) {
    return null;
  }

  const trimmed = nextPath.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return null;
  }

  return trimmed;
};

export const getPostAuthRedirectPath = (
  nextPath?: string | null,
  fallbackPath = "/users"
): string => sanitizeNextPath(nextPath) ?? fallbackPath;

export const getLoginRedirectPath = (nextPath?: string | null): string => {
  const safeNextPath = sanitizeNextPath(nextPath);
  if (!safeNextPath) {
    return "/login";
  }

  return `/login?next=${encodeURIComponent(safeNextPath)}`;
};

export const isValidApiBaseUrl = (apiBaseUrl?: string | null): boolean => {
  if (!apiBaseUrl) {
    return false;
  }

  try {
    // Validates absolute URL for safe OAuth endpoint construction.
    const parsed = new URL(apiBaseUrl);
    return Boolean(parsed.protocol && parsed.host);
  } catch {
    return false;
  }
};

export const buildGoogleOAuthHref = (
  apiBaseUrl?: string | null,
  nextPath?: string | null
): string | null => {
  if (!isValidApiBaseUrl(apiBaseUrl)) {
    return null;
  }

  const oauthUrl = new URL("/auth/google", apiBaseUrl as string);
  oauthUrl.searchParams.set("app", "admindashboard");

  const safeNextPath = sanitizeNextPath(nextPath);
  if (safeNextPath) {
    oauthUrl.searchParams.set("next", safeNextPath);
  }

  return oauthUrl.toString();
};

const dispatchStorageUpdate = (key: string, oldValue: string | null, newValue: string | null) => {
  window.dispatchEvent(
    new StorageEvent("storage", {
      key,
      oldValue,
      newValue,
    })
  );
};

export const setStoredAuthToken = (token: string) => {
  const previousToken = localStorage.getItem("token");
  localStorage.setItem("token", token);
  dispatchStorageUpdate("token", previousToken, token);
};

export const persistAuthSession = <User extends { access_token?: string }>(
  token: string,
  user: User
) => {
  const previousAuthStatus = localStorage.getItem("isAuthenticated");
  setStoredAuthToken(token);
  localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("isAuthenticated", "true");
  dispatchStorageUpdate("isAuthenticated", previousAuthStatus, "true");
};

export const persistAuthSessionFromUser = <User extends AuthenticatedUser>(user: User) => {
  persistAuthSession(user.access_token, user);
};

export const processSocialLoginToken = async <User>(
  token: string | null,
  nextPath: string | null | undefined,
  deps: SocialLoginDeps<User>
): Promise<string> => {
  if (!token) {
    return getLoginRedirectPath(nextPath);
  }

  try {
    deps.setAuthToken(token);
    const user = await deps.fetchCurrentUser();
    deps.persistSession(token, user);
    return getPostAuthRedirectPath(nextPath);
  } catch {
    deps.clearAuth();
    return getLoginRedirectPath(nextPath);
  }
};
